import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import WaveSurfer from "wavesurfer.js";
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
} from "@tabler/icons-react";
import AppModal from "@components/modals/AppModal";
import InlineError from "@components/forms/InlineError";

function AudioWavePreview({ url = "" }) {
  const hostRef = useRef(null);
  const waveRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!hostRef.current || !url) return;
    const wave = WaveSurfer.create({
      container: hostRef.current,
      url,
      height: 32,
      barWidth: 2,
      barGap: 1,
      cursorWidth: 1,
      waveColor: "#94a3b8",
      progressColor: "#0ea5e9",
      cursorColor: "#f8fafc",
      normalize: true,
    });
    waveRef.current = wave;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onFinish = () => setIsPlaying(false);
    wave.on("play", onPlay);
    wave.on("pause", onPause);
    wave.on("finish", onFinish);

    return () => {
      wave.un("play", onPlay);
      wave.un("pause", onPause);
      wave.un("finish", onFinish);
      wave.destroy();
      waveRef.current = null;
    };
  }, [url]);

  return (
    <div className="tour-audio-wave-preview">
      <button
        type="button"
        className="app-icon-btn app-icon-btn--update tour-audio-wave-play"
        onClick={(event) => {
          event.stopPropagation();
          waveRef.current?.playPause();
        }}
        aria-label={isPlaying ? "Pause audio preview" : "Play audio preview"}
        title={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <IconPlayerPause size={14} />
        ) : (
          <IconPlayerPlay size={14} />
        )}
      </button>
      <div
        ref={hostRef}
        className="tour-audio-wave-host"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      />
    </div>
  );
}

function MediaPicker({
  title = "multimedia",
  titleError = "",
  items = [],
  onUpload,
  onRemove,
  isUploading = false,
  uploadError = "",
  accept = { "image/*": [], "video/*": [] },
  maxFiles = 10,
  uploadLabel = "Drag media here or click to upload",
  uploadHint = "Supports one or multiple files (maximum 10 per upload).",
  invalidTypeMessage = "Only images, videos, or GIF files are allowed in this section.",
  onRejected,
  renderItemExtra,
  onReorder,
}) {
  const [preview, setPreview] = useState({
    isOpen: false,
    url: "",
    mediaType: "",
    mimeType: "",
  });
  const [isUploadingLocal, setIsUploadingLocal] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState("");
  const isBusy = Boolean(isUploading || isUploadingLocal);

  function openPreview(item) {
    setPreview({
      isOpen: true,
      url: item?.url || "",
      mediaType: item?.mediaType || "",
      mimeType: item?.mimeType || "",
    });
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    maxFiles,
    disabled: isBusy,
    accept,
    onDropRejected: () => {
      if (onRejected) onRejected(invalidTypeMessage);
    },
    onDrop: async (acceptedFiles) => {
      if (!onUpload) return;
      setIsUploadingLocal(true);
      try {
        await onUpload(acceptedFiles);
      } finally {
        setIsUploadingLocal(false);
      }
    },
  });

  return (
    <div className="tour-add-form-group">
      <p className="tour-add-form-label">
        {title}
        {titleError ? <InlineError>{titleError}</InlineError> : null}
      </p>

      <div
        {...getRootProps()}
        className={`tour-add-media-dropzone ${isDragActive ? "active" : ""} ${isBusy ? "disabled" : ""}`}
        aria-busy={isBusy}
      >
        <input {...getInputProps()} />
        <p>
          {isBusy
            ? "Uploading files..."
            : isDragActive
              ? "Drop files here..."
              : uploadLabel}
        </p>
        <small>{uploadHint}</small>
        {isBusy ? (
          <div className="tour-add-media-loading">
            <span className="tour-add-media-loading-spinner" />
            <span>Uploading multimedia, please wait...</span>
          </div>
        ) : null}

        {uploadError ? (
          <p className="tour-add-media-error">{uploadError}</p>
        ) : null}

        <div
          className="tour-add-media-grid"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {items.map((item) => (
            <div
              key={item.id}
              draggable={Boolean(onReorder) && !isBusy}
              className={`tour-add-media-card ${
                item.mediaType === "audio" ||
                (item.mimeType || "").startsWith("audio/")
                  ? "tour-add-media-card-audio"
                  : ""
              } ${onReorder && !isBusy ? "tour-add-media-card-reorderable" : ""}`}
              onDragStart={(event) => {
                if (!onReorder || isBusy) return;
                event.stopPropagation();
                setDraggedItemId(String(item.id || ""));
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(item.id || ""));
              }}
              onDragOver={(event) => {
                if (!onReorder || !draggedItemId) return;
                event.preventDefault();
                event.stopPropagation();
                event.dataTransfer.dropEffect = "move";
              }}
              onDrop={(event) => {
                if (!onReorder || !draggedItemId) return;
                event.preventDefault();
                event.stopPropagation();
                const sourceId = draggedItemId;
                const targetId = String(item.id || "");
                setDraggedItemId("");
                if (!sourceId || sourceId === targetId) return;
                const nextItems = [...items];
                const sourceIndex = nextItems.findIndex(
                  (entry) => String(entry?.id || "") === sourceId,
                );
                const targetIndex = nextItems.findIndex(
                  (entry) => String(entry?.id || "") === targetId,
                );
                if (sourceIndex < 0 || targetIndex < 0) return;
                const [movedItem] = nextItems.splice(sourceIndex, 1);
                nextItems.splice(targetIndex, 0, movedItem);
                onReorder(nextItems);
              }}
              onDragEnd={() => setDraggedItemId("")}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                if (
                  item.mediaType === "audio" ||
                  (item.mimeType || "").startsWith("audio/")
                )
                  return;
                openPreview(item);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  if (
                    item.mediaType === "audio" ||
                    (item.mimeType || "").startsWith("audio/")
                  )
                    return;
                  openPreview(item);
                }
              }}
            >
              <div
                className="tour-add-media-preview"
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    item.mediaType === "audio" ||
                    (item.mimeType || "").startsWith("audio/")
                  )
                    return;
                  openPreview(item);
                }}
              >
                {item.mediaType === "image" ||
                (item.mimeType || "").startsWith("image/") ? (
                  <img
                    src={item.url}
                    alt={item.id}
                    className="tour-add-media-thumb"
                  />
                ) : item.mediaType === "video" ||
                  (item.mimeType || "").startsWith("video/") ? (
                  <video
                    className="tour-add-media-thumb"
                    muted
                    preload="metadata"
                  >
                    <source src={item.url} type={item.mimeType || undefined} />
                  </video>
                ) : item.mediaType === "audio" ||
                  (item.mimeType || "").startsWith("audio/") ? (
                  <AudioWavePreview url={item.url} />
                ) : (
                  <div className="tour-add-media-fallback">FILE</div>
                )}
                {!(
                  item.mediaType === "audio" ||
                  (item.mimeType || "").startsWith("audio/")
                ) ? (
                  <button
                    type="button"
                    className="app-icon-btn app-icon-btn--delete tour-add-media-delete-overlay"
                    disabled={isBusy}
                    aria-label="Delete media"
                    title="Delete media"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove?.(item.id);
                    }}
                  >
                    <IconTrash size={14} />
                  </button>
                ) : null}
              </div>
              {/*    <div className="tour-add-media-meta">
                {item.mediaType === 'audio' || (item.mimeType || '').startsWith('audio/') ? (
                  <button
                    type="button"
                    className="app-icon-btn app-icon-btn--delete tour-add-media-delete-inline"
                    disabled={isBusy}
                    aria-label="Delete media"
                    title="Delete media"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove?.(item.id);
                    }}
                  >
                    <IconTrash size={14} />
                  </button>
                ) : null}
              </div> */}
              {typeof renderItemExtra === "function" ? (
                <div className="tour-add-media-extra">
                  {renderItemExtra(item)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <AppModal
        isOpen={preview.isOpen}
        onClose={() =>
          setPreview({ isOpen: false, url: "", mediaType: "", mimeType: "" })
        }
        title="Media Preview"
        size="xl"
      >
        {preview.mediaType === "video" ||
        preview.mimeType.startsWith("video/") ? (
          <video className="tour-add-media-modal-video" controls autoPlay>
            <source src={preview.url} type={preview.mimeType || undefined} />
          </video>
        ) : preview.mediaType === "audio" ||
          preview.mimeType.startsWith("audio/") ? (
          <audio className="tour-add-media-modal-audio" controls autoPlay>
            <source src={preview.url} type={preview.mimeType || undefined} />
          </audio>
        ) : (
          <img
            src={preview.url}
            alt="Media preview"
            className="tour-add-media-modal-image"
          />
        )}
      </AppModal>
    </div>
  );
}

export default MediaPicker;
